import React, { useState } from 'react';
import { Paper, Box, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import FannedThumbnails, { type FannedThumbnailImage } from 'components/atomic/molecules/fanned-thumbnails';

const SERIF = 'var(--themes-fonts-heading), Georgia, serif';
const BODY = 'var(--themes-fonts-text), sans-serif';

export interface AllSavedSearchesButtonProps {
  /** Thumbnails for the fan, in fan order (left, right, center). */
  images: FannedThumbnailImage[];
  title: string;
  subtitle: string;
  onClick: () => void;
  /** sx merged onto the button (e.g. an outer margin). */
  sx?: SxProps<Theme>;
}

/**
 * AllSavedSearchesButton
 *
 * Domain component (Layer 2, `src/ui/`). A full-width nav row into the saved
 * searches page: a fan of thumbnails that spreads open on hover/focus, a title +
 * subtitle, and a trailing chevron. Receives pre-formatted strings (no i18n) and
 * owns only its hover state; composes the `FannedThumbnails` molecule.
 */
export function AllSavedSearchesButton({ images, title, subtitle, onClick, sx }: AllSavedSearchesButtonProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <Paper
      component="button"
      type="button"
      elevation={0}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      sx={[
        {
          display: 'block',
          width: '100%',
          p: 1.5,
          textAlign: 'left',
          cursor: 'pointer',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          transition: 'background-color 150ms ease',
          '&:hover': { backgroundColor: '#f9fafb' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <FannedThumbnails images={images} hovered={hovered} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: SERIF, fontSize: '1rem', fontWeight: 400, color: '#0F1729' }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: BODY, fontSize: 12, color: '#9ca3af' }}>{subtitle}</Typography>
        </Box>
        <ChevronRight size={18} color="#9ca3af" style={{ flexShrink: 0 }} />
      </Stack>
    </Paper>
  );
}

export default AllSavedSearchesButton;
